import React, {
  forwardRef,
  ComponentPropsWithRef,
  ElementType,
  ForwardRefExoticComponent,
  JSX,
  ReactElement,
} from 'react';
import classNames from 'classnames';

import 'app/assets/stylesheets/design_system/mono.scss';

export type BaseMonoProps = {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

type Merge<P1 = object, P2 = object> = Omit<P1, keyof P2> & P2;
type MergeProps<E, P = object> = P &
  Merge<E extends ElementType ? ComponentPropsWithRef<E> : never, P>;

interface ForwardRefComponent<IntrinsicElementString, OwnProps = object>
  extends ForwardRefExoticComponent<
    MergeProps<
      IntrinsicElementString,
      OwnProps & { as?: IntrinsicElementString }
    >
  > {
  <As extends keyof JSX.IntrinsicElements>(
    props: MergeProps<As, OwnProps & { as: As }>,
  ): ReactElement | null;

  <
    As extends ElementType<unknown>,
    _AsWithProps = As extends ElementType<infer P> ? ElementType<P> : never,
  >(
    props: MergeProps<_AsWithProps, OwnProps & { as: _AsWithProps }>,
  ): ReactElement | null;
}

/**
 * Polymorphic component for monospace-styled text.
 * Used for data & labels.
 *
 * Props:
 * - `size`: Controls the font-size and line-height of the text (default: 'md')
 *           Choose between 'sm' | 'md' | 'lg'
 * - `as`:   The element type to render as. Defaults to 'p'.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Mono } from 'js/design_system/Mono';
 *
 * <Mono>A simple monospace paragraph</Mono>
 * ```
 * ---
 *
 * ## Custom Size
 *
 * @example
 *
 * ```tsx
 * import { Mono } from 'js/design_system/Mono';
 *
 * <Mono size='lg'>Bigger mono text</Mono>
 * ```
 * ---
 *
 */
export const Mono = forwardRef(
  (
    {
      as = 'p',
      size = 'md',
      className,
      children,
      ...restProps
    }: React.PropsWithChildren<BaseMonoProps> & {
      as: ElementType;
    },
    ref,
  ) => {
    const Tag = as ?? 'p';

    const classes = classNames(className, `ds2-5-mono-${size}`, {
      'ds2-5-mono-link': as === 'button',
    });

    return (
      <Tag className={classes} {...restProps} ref={ref}>
        {children}
      </Tag>
    );
  },
) as ForwardRefComponent<ElementType, React.PropsWithChildren<BaseMonoProps>>;
